// LandingPage.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser, SignIn } from '@clerk/clerk-react';
import { Button } from '../../components/ui/button.jsx';
import { Input } from '../../components/ui/input.jsx';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion.jsx";
import { Menu, X } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation, Autoplay } from 'swiper/modules';
import { motion, useAnimation, useInView } from 'framer-motion';
import './styles/navbar.css';
import './styles/App.css';
import './styles/index.css';
import './styles/storybook-theme.css';
import axios from 'axios';
import { Switch } from '../../components/ui/switch.jsx';
import AnimatedUnderline from './components/AnimatedUnderline.js';
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog.jsx';

// New interactive components
import LandingStorybookPreview from './components/LandingStorybookPreview.js';
import CharacterBuilderTeaser from './components/CharacterBuilderTeaser.js';
import VoiceSamplesPlayer from './components/VoiceSamplesPlayer.js';
import WizardStepsSection from './components/WizardStepsSection.js';
import FinalCTASection from './components/FinalCTASection.js';

const LandingPage = () => {
    const { isSignedIn } = useUser();
    const navigate = useNavigate();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSignInOpen, setIsSignInOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [message, setMessage] = useState('');
    const [isYearly, setIsYearly] = useState(false);

    const controls = useAnimation();
    const benefitsRef = useRef(null);
    const isInView = useInView(benefitsRef, { once: true });

    useEffect(() => {
      if (isInView) {
        controls.start('visible');
      }
    }, [isInView, controls]);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setMessage('');

      try {
        const response = await axios.post('/api/subscribe-waitlist', {
          email: email,
          name: name
        });

        setMessage(response.data.message || 'Thank you for joining our waitlist!');
        setEmail('');
        setName('');
      } catch (error) {
        const errorMessage = error.response?.data?.error || 'An error occurred. Please try again later.';
        setMessage(errorMessage);
      }
    };

    const openWaitlistModal = () => setIsModalOpen(true);

    const openStoryMaker = () => {
      setIsMenuOpen(false);
      if (isSignedIn) {
        navigate('/wizard');
      } else {
        setIsSignInOpen(true);
      }
    };

    const pricingPlans = [
      {
        plan: 'Basic',
        monthlyPrice: 4.99,
        yearlyPrice: 49.99,
        features: [
          '5 Stories per Month',
          '3 Regenerations per Story',
          '5 Saved Characters',
          { text: 'No Audio Reading', negative: true }
        ]
      },
      {
        plan: 'Premium',
        monthlyPrice: 9.99,
        yearlyPrice: 99.99,
        features: [
          '30 Stories per Month',
          '10 Regenerations Per Story',
          '10 Saved Characters',
          'Basic Audio Reading'
        ]
      },
      {
        plan: 'Premium Plus',
        monthlyPrice: 19.99,
        yearlyPrice: 199.99,
        features: [
          'Unlimited Story Generations',
          'Unlimited Regenerations Per Story',
          'Unlimited Saved Characters',
          'Enhanced Audio Reading'
        ]
      }
    ];

    const calculateYearlyDiscount = (monthlyPrice, yearlyPrice) => {
      const yearlyTotal = monthlyPrice * 12;
      const discount = ((yearlyTotal - yearlyPrice) / yearlyTotal) * 100;
      return Math.round(discount);
    };

    const Divider = () => (
      <div className="my-4 border-t border-gray-200"></div>
    );

    const testimonials = [{
        name: 'Emily R.',
        role: 'Mother of two',
        quote: 'Make It Mindful has transformed our bedtime routine. My kids look forward to their nightly story!',
        image: '/landing/images/testimonials/emily.png'
      }, {
        name: 'Michael S.',
        role: 'Father',
        quote: 'The personalized stories have helped my child deal with anxiety in a fun, engaging way.',
        image: '/landing/images/testimonials/michael.png'
      }, {
        name: 'Sarah L.',
        role: 'Parent',
        quote: 'As a busy parent, I love how easy it is to create meaningful stories that my kids adore.',
        image: '/landing/images/testimonials/sarah.png'
      }, {
        name: 'Jane K.',
        role: 'Mother of a toddler',
        quote: 'I love how Make It Mindful helps my daughter relax before sleep. The stories are perfect for her age.',
        image: '/landing/images/testimonials/jane.png'
      }, {
        name: 'David M.',
        role: 'Father of three',
        quote: 'The stories are engaging and have become a favorite part of our family time.',
        image: '/landing/images/testimonials/david.png'
      }, {
        name: 'Laura P.',
        role: 'Parent',
        quote: 'A wonderful way to bond with my children while teaching them mindfulness.',
        image: '/landing/images/testimonials/laura.png'
    }];

    return (
      <div className="min-h-screen flex flex-col font-sans bg-storybook paper-texture">
        {/* Navbar */}
        <nav className="bg-[#FFFBF7] shadow-sm sticky top-0 z-50 border-b border-[#E8DFD5]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              {/* Left Group: Logo + Title */}
              <div className="flex items-center">
                <img
                  src="/landing/images/favicon.ico"
                  alt="Make It Mindful Logo"
                  className="h-8 w-8 mr-2"
                />
                <div>
                  <Link to="/" className="text-2xl font-bold font-display text-[#3D3832] hover:text-[#7B68EE] transition-colors duration-300">
                    Make It Mindful
                  </Link>
                </div>
              </div>

              {/* Right Group (Desktop): Links + account actions */}
              <div className="hidden sm:flex items-center gap-6">
                <div className="flex items-center gap-6">
                  <a href="#story-preview" className="text-[#5C534A] hover:text-[#7B68EE] text-sm font-medium transition-colors">Try Demo</a>
                  <a href="#how-it-works" className="text-[#5C534A] hover:text-[#7B68EE] text-sm font-medium transition-colors">How it Works</a>
                  <a href="#pricing" className="text-[#5C534A] hover:text-[#7B68EE] text-sm font-medium transition-colors">Pricing</a>
                  <a href="#faq" className="text-[#5C534A] hover:text-[#7B68EE] text-sm font-medium transition-colors">FAQs</a>
                </div>
                {isSignedIn ? (
                  <Link
                    to="/wizard"
                    className="text-[#5C534A] hover:text-[#7B68EE] text-sm font-semibold transition-colors"
                  >
                    Open App
                  </Link>
                ) : (
                  <Button
                    variant="outline"
                    className="border-[#7B68EE] text-[#5B4BC7] hover:bg-[#F5EDE3] px-4 py-2 rounded-full"
                    onClick={() => setIsSignInOpen(true)}
                  >
                    Log in
                  </Button>
                )}
                <Button
                  className="bg-gradient-to-r from-[#7B68EE] to-[#9B8AC4] hover:from-[#5B4BC7] hover:to-[#8B7AB8] text-white px-5 py-2 rounded-full shadow-lg transition-all transform hover:scale-105 hover:shadow-xl duration-300"
                  onClick={openStoryMaker}
                >
                  Create a Story
                </Button>
              </div>

              {/* Right Group (Mobile): Hamburger */}
              <div className="sm:hidden flex items-center">
                <button
                  type="button"
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  aria-expanded={isMenuOpen}
                  aria-controls="landing-mobile-menu"
                  aria-label={isMenuOpen ? 'Close main menu' : 'Open main menu'}
                  className="inline-flex items-center justify-center p-2 rounded-md text-[#8B8279] hover:text-[#3D3832] hover:bg-[#F5EDE3] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#7B68EE]"
                >
                  {isMenuOpen ? (
                    <X className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Menu className="block h-6 w-6" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Mobile menu */}
          {isMenuOpen && (
            <div id="landing-mobile-menu" className="sm:hidden bg-[#FFFBF7]">
              <div className="px-2 pt-2 pb-3 space-y-1">
                <a href="#story-preview" onClick={() => setIsMenuOpen(false)} className="text-[#5C534A] hover:bg-[#F5EDE3] hover:text-[#7B68EE] block px-3 py-2 rounded-md text-base font-medium">Try Demo</a>
                <a href="#how-it-works" onClick={() => setIsMenuOpen(false)} className="text-[#5C534A] hover:bg-[#F5EDE3] hover:text-[#7B68EE] block px-3 py-2 rounded-md text-base font-medium">How it Works</a>
                <a href="#pricing" onClick={() => setIsMenuOpen(false)} className="text-[#5C534A] hover:bg-[#F5EDE3] hover:text-[#7B68EE] block px-3 py-2 rounded-md text-base font-medium">Pricing</a>
                <a href="#faq" onClick={() => setIsMenuOpen(false)} className="text-[#5C534A] hover:bg-[#F5EDE3] hover:text-[#7B68EE] block px-3 py-2 rounded-md text-base font-medium">FAQs</a>
                <button
                  type="button"
                  onClick={isSignedIn ? openStoryMaker : () => { setIsSignInOpen(true); setIsMenuOpen(false); }}
                  className="text-[#5C534A] hover:bg-[#F5EDE3] hover:text-[#7B68EE] block w-full text-left px-3 py-2 rounded-md text-base font-semibold"
                >
                  {isSignedIn ? 'Open App' : 'Log in'}
                </button>
                <Button
                  className="w-full bg-gradient-to-r from-[#7B68EE] to-[#9B8AC4] hover:from-[#5B4BC7] hover:to-[#8B7AB8] text-white px-4 py-2 rounded-full shadow-lg transition-all transform hover:scale-105 duration-300 mt-2"
                  onClick={openStoryMaker}
                >
                  Create a Story
                </Button>
              </div>
            </div>
          )}
        </nav>

        {/* Main content */}
        <main className="flex-grow">

          {/* Hero Section */}
          <section className="py-20 px-4 sm:px-6 lg:px-8 section-coral relative overflow-hidden">
            {/* Decorative floating elements */}
            <motion.div
              className="absolute top-20 left-10 w-16 h-16 opacity-20"
              animate={{ y: [0, -15, 0], rotate: [0, 10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            >
              <svg viewBox="0 0 100 100" className="w-full h-full text-[#7B68EE]">
                <path d="M50 0 L61 35 L98 35 L68 57 L79 91 L50 70 L21 91 L32 57 L2 35 L39 35 Z" fill="currentColor"/>
              </svg>
            </motion.div>
            <motion.div
              className="absolute top-40 right-16 w-12 h-12 opacity-15"
              animate={{ y: [0, 10, 0], rotate: [0, -15, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            >
              <svg viewBox="0 0 100 100" className="w-full h-full text-[#9B8AC4]">
                <circle cx="50" cy="50" r="45" fill="currentColor"/>
              </svg>
            </motion.div>
            <motion.div
              className="absolute bottom-20 left-1/4 w-20 h-20 opacity-10"
              animate={{ y: [0, -20, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 2 }}
            >
              <svg viewBox="0 0 100 100" className="w-full h-full text-[#8B9FD9]">
                <path d="M50 10 Q90 50 50 90 Q10 50 50 10" fill="currentColor"/>
              </svg>
            </motion.div>

            <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center relative z-10">
              <div className="md:w-1/2 text-left mb-8 md:mb-0 pr-0 md:pr-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h1 className="text-[#3D3832] text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight font-display">
                    Stories as Unique as{' '}
                    <AnimatedUnderline
                      text="Your Child"
                      color="#7B68EE"
                      fontSize="inherit"
                      className="font-bold"
                    />
                  </h1>
                  <p className="text-[#5C534A] text-lg md:text-xl mt-6 font-body">
                    Create personalized, illustrated storybooks with AI. Mindfulness woven into every page. 6 narration voices to choose from.
                  </p>
                  <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 items-center mt-8">
                    <Button
                      className="bg-gradient-to-r from-[#7B68EE] to-[#9B8AC4] hover:from-[#5B4BC7] hover:to-[#8B7AB8] text-white rounded-full px-8 py-4 text-lg transition-transform transform hover:scale-105 shadow-lg hover:shadow-xl w-full sm:w-auto"
                      onClick={openStoryMaker}
                    >
                      Create your first story
                    </Button>
                    <a
                      href="#story-preview"
                      className="text-[#7B68EE] hover:text-[#5B4BC7] font-medium flex items-center gap-2 transition-colors"
                    >
                      Try the demo below
                      <motion.span
                        animate={{ y: [0, 5, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      >↓</motion.span>
                    </a>
                  </div>
                </motion.div>
              </div>
              <div className="md:w-1/2 mt-8 md:mt-0">
                <motion.div
                  className="relative overflow-hidden rounded-3xl shadow-2xl page-edge"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  whileHover={{ scale: 1.02 }}
                >
                  <img
                    src="/landing/images/family_reading.png"
                    alt="Family reading together"
                    className="w-full"
                  />
                  {/* Floating badge */}
                  <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-sm rounded-full px-4 py-2 shadow-lg border border-[#E8DFD5]">
                    <span className="text-sm font-medium text-[#3D3832]">✨ AI-Generated Illustrations</span>
                  </div>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Interactive Story Preview */}
          <div id="story-preview">
            <LandingStorybookPreview onJoinWaitlist={openWaitlistModal} />
          </div>

          {/* Character Builder Teaser */}
          <CharacterBuilderTeaser />

          {/* Voice Samples Player */}
          <VoiceSamplesPlayer />

          {/* Wizard Steps (How It Works) */}
          <WizardStepsSection />

          <Divider />

          {/* Testimonials */}
          <section className="py-12 section-lavender relative overflow-hidden">
            <div className="max-w-[90%] mx-auto px-4 sm:px-6 lg:px-8 relative">
              <h2 className="section-header mb-8">What Parents Are Saying</h2>
              <div className="relative">
                <Swiper
                  modules={[Pagination, Navigation, Autoplay]}
                  slidesPerView={3}
                  spaceBetween={30}
                  pagination={{ clickable: true }}
                  navigation
                  autoplay={{ delay: 5000, disableOnInteraction: false }}
                  loop={true}
                  breakpoints={{
                    0: { slidesPerView: 1, spaceBetween: 10 },
                    640: { slidesPerView: 2, spaceBetween: 20 },
                    1024: { slidesPerView: 3, spaceBetween: 30 }
                  }}
                  className="pb-10"
                >
                  {testimonials.map((testimonial, index) => (
                    <SwiperSlide key={index} style={{ display: 'flex', justifyContent: 'center' }}>
                      <div className="storybook-card p-6 w-full max-w-sm mx-auto flex flex-col items-center justify-between h-[300px]">
                        <img className="h-16 w-16 rounded-full mb-4 object-cover shadow-md border-2 border-[#E8DFD5]" src={testimonial.image} alt={testimonial.name} />
                        <div className="flex-grow flex flex-col justify-between">
                          <p className="italic text-[#5C534A] mb-4 text-center">"{testimonial.quote}"</p>
                          <div className="text-center">
                            <p className="font-semibold text-[#3D3832]">{testimonial.name}</p>
                            <p className="text-[#8B8279] text-sm">{testimonial.role}</p>
                          </div>
                        </div>
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </div>
          </section>

          <Divider />

          {/* Pricing Section */}
          <section id="pricing" className="py-12 section-amber">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="section-header mb-4">Pricing and Plans</h2>
              <p className="text-center text-[#5C534A] mb-8 max-w-2xl mx-auto">
                Choose the perfect plan to unlock the full potential of our mindful storytelling app.
              </p>

              <div className="flex justify-center items-center mb-8">
                <span className={`mr-3 ${isYearly ? 'text-[#8B8279]' : 'font-semibold text-[#3D3832]'}`}>Monthly</span>
                <Switch checked={isYearly} onCheckedChange={setIsYearly} />
                <span className={`ml-3 ${isYearly ? 'font-semibold text-[#3D3832]' : 'text-[#8B8279]'}`}>Yearly</span>
              </div>

              <div className="px-0 md:px-[5%] lg:px-[10%]">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4 justify-center">
                  {pricingPlans.map((plan, index) => (
                    <motion.div
                      key={index}
                      className={`storybook-card p-6 flex flex-col justify-between w-full max-w-sm mx-auto md:max-w-[300px]
                        ${index === 1
                          ? 'border-2 border-[#7B68EE] md:transform md:scale-105 shadow-xl'
                          : ''
                        }`}
                      whileHover={{ y: -5 }}
                    >
                      {index === 1 && (
                        <div className="bg-gradient-to-r from-[#7B68EE] to-[#9B8AC4] text-white text-xs font-semibold px-3 py-1 rounded-full inline-block mb-3 mx-auto">
                          MOST POPULAR
                        </div>
                      )}
                      <div>
                        <h3 className="text-lg font-semibold mb-3 text-center text-[#3D3832]">{plan.plan}</h3>
                        <div className="mb-3 text-center">
                          {isYearly && (
                            <span className="text-[#8B8279] line-through text-sm mr-1">
                              ${(plan.monthlyPrice * 12).toFixed(2)}
                            </span>
                          )}
                          <span className="text-2xl font-bold text-[#3D3832]">
                            ${isYearly ? plan.yearlyPrice.toFixed(2) : plan.monthlyPrice.toFixed(2)}
                          </span>
                          <span className="text-[#8B8279] text-sm">/{isYearly ? 'Year' : 'Month'}</span>
                        </div>
                        {isYearly && (
                          <p className="text-xs text-[#7DB896] font-medium mb-3 text-center">
                            Save {calculateYearlyDiscount(plan.monthlyPrice, plan.yearlyPrice)}% with yearly billing
                          </p>
                        )}
                        <ul className="space-y-2 mb-4 text-sm">
                          {plan.features.map((feature, featureIndex) => (
                            <li key={featureIndex} className="flex items-center">
                              {typeof feature === 'string' ? (
                                <>
                                  <svg className="w-4 h-4 mr-2 text-[#7DB896] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  <span className="text-[#5C534A]">{feature}</span>
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4 mr-2 text-[#E8A0B4] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                  <span className="text-[#8B8279]">{feature.text}</span>
                                </>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <Link
                        to={`/pricing?plan=${plan.plan.toLowerCase().replace(' ', '_')}&billing=${isYearly ? 'yearly' : 'monthly'}`}
                        className={`w-full py-2 px-4 rounded-full text-sm text-center block transition-all ${
                          index === 1
                            ? 'bg-gradient-to-r from-[#7B68EE] to-[#9B8AC4] text-white hover:shadow-lg'
                            : 'bg-[#FFFBF7] text-[#7B68EE] border-2 border-[#7B68EE] hover:bg-[#E8E4F8]'
                        }`}
                      >
                        Get Started
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <Divider />

          {/* FAQ Section */}
          <section id="faq" className="py-12 section-sage">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="section-header mb-12">Frequently Asked Questions</h2>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                {/* Category 1: General Information */}
                <div>
                  <h3 className="text-xl font-bold mb-6 text-[#3D3832]">General Information</h3>
                  <Accordion type="single" collapsible className="space-y-4">
                    <AccordionItem value="faq-1" className="border border-[#E8DFD5] rounded-lg overflow-hidden bg-[#FFFBF7]">
                      <AccordionTrigger className="text-left px-4 py-3 hover:bg-[#F5EDE3] flex items-center justify-between text-sm text-[#3D3832]">
                        What ages is this app suitable for?
                      </AccordionTrigger>
                      <AccordionContent className="px-4 py-3 bg-[#F5EDE3] text-sm text-[#5C534A]">
                        Make It Mindful is designed for children aged 3-12, but the stories can be enjoyed by the whole family.
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="faq-2" className="border border-[#E8DFD5] rounded-lg overflow-hidden bg-[#FFFBF7]">
                      <AccordionTrigger className="text-left px-4 py-3 hover:bg-[#F5EDE3] flex items-center justify-between text-sm text-[#3D3832]">
                        How personalized are the stories?
                      </AccordionTrigger>
                      <AccordionContent className="px-4 py-3 bg-[#F5EDE3] text-sm text-[#5C534A]">
                        Very! You can customize characters with personality traits, fears, dreams, and interests. Stories are generated to reflect these details.
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="faq-3" className="border border-[#E8DFD5] rounded-lg overflow-hidden bg-[#FFFBF7]">
                      <AccordionTrigger className="text-left px-4 py-3 hover:bg-[#F5EDE3] flex items-center justify-between text-sm text-[#3D3832]">
                        How do the AI illustrations work?
                      </AccordionTrigger>
                      <AccordionContent className="px-4 py-3 bg-[#F5EDE3] text-sm text-[#5C534A]">
                        Our AI generates unique illustrations for each page of your story, matching the characters and scenes you've created.
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="faq-4" className="border border-[#E8DFD5] rounded-lg overflow-hidden bg-[#FFFBF7]">
                      <AccordionTrigger className="text-left px-4 py-3 hover:bg-[#F5EDE3] flex items-center justify-between text-sm text-[#3D3832]">
                        Can I hear the narration voices before subscribing?
                      </AccordionTrigger>
                      <AccordionContent className="px-4 py-3 bg-[#F5EDE3] text-sm text-[#5C534A]">
                        Yes! Scroll up to the Voice Samples section to hear all 6 available narration voices.
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>

                {/* Category 2: Technical Support */}
                <div>
                  <h3 className="text-xl font-bold mb-6 text-[#3D3832]">Technical Support</h3>
                  <Accordion type="single" collapsible className="space-y-4">
                    <AccordionItem value="faq-5" className="border border-[#E8DFD5] rounded-lg overflow-hidden bg-[#FFFBF7]">
                      <AccordionTrigger className="text-left px-4 py-3 hover:bg-[#F5EDE3] flex items-center justify-between text-sm text-[#3D3832]">
                        I forgot my password. How can I reset it?
                      </AccordionTrigger>
                      <AccordionContent className="px-4 py-3 bg-[#F5EDE3] text-sm text-[#5C534A]">
                        Click the "Forgot Password" link on the login page and follow the instructions sent to your email.
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="faq-6" className="border border-[#E8DFD5] rounded-lg overflow-hidden bg-[#FFFBF7]">
                      <AccordionTrigger className="text-left px-4 py-3 hover:bg-[#F5EDE3] flex items-center justify-between text-sm text-[#3D3832]">
                        Can I use Make It Mindful on multiple devices?
                      </AccordionTrigger>
                      <AccordionContent className="px-4 py-3 bg-[#F5EDE3] text-sm text-[#5C534A]">
                        Yes! Access Make It Mindful on any device by logging in with your account credentials.
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="faq-7" className="border border-[#E8DFD5] rounded-lg overflow-hidden bg-[#FFFBF7]">
                      <AccordionTrigger className="text-left px-4 py-3 hover:bg-[#F5EDE3] flex items-center justify-between text-sm text-[#3D3832]">
                        Do I need to download anything?
                      </AccordionTrigger>
                      <AccordionContent className="px-4 py-3 bg-[#F5EDE3] text-sm text-[#5C534A]">
                        No! Make It Mindful is a web app. Just visit the website on any browser to start creating stories.
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="faq-8" className="border border-[#E8DFD5] rounded-lg overflow-hidden bg-[#FFFBF7]">
                      <AccordionTrigger className="text-left px-4 py-3 hover:bg-[#F5EDE3] flex items-center justify-between text-sm text-[#3D3832]">
                        What if the app isn't working correctly?
                      </AccordionTrigger>
                      <AccordionContent className="px-4 py-3 bg-[#F5EDE3] text-sm text-[#5C534A]">
                        Try refreshing the page or clearing your browser cache. If issues persist, contact our support team.
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>

                {/* Category 3: Subscription & Billing */}
                <div>
                  <h3 className="text-xl font-bold mb-6 text-[#3D3832]">Subscription & Billing</h3>
                  <Accordion type="single" collapsible className="space-y-4">
                    <AccordionItem value="faq-9" className="border border-[#E8DFD5] rounded-lg overflow-hidden bg-[#FFFBF7]">
                      <AccordionTrigger className="text-left px-4 py-3 hover:bg-[#F5EDE3] flex items-center justify-between text-sm text-[#3D3832]">
                        Is there a story limit?
                      </AccordionTrigger>
                      <AccordionContent className="px-4 py-3 bg-[#F5EDE3] text-sm text-[#5C534A]">
                        Basic plans have monthly limits. Premium Plus offers unlimited story creation.
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="faq-10" className="border border-[#E8DFD5] rounded-lg overflow-hidden bg-[#FFFBF7]">
                      <AccordionTrigger className="text-left px-4 py-3 hover:bg-[#F5EDE3] flex items-center justify-between text-sm text-[#3D3832]">
                        Can I cancel my subscription?
                      </AccordionTrigger>
                      <AccordionContent className="px-4 py-3 bg-[#F5EDE3] text-sm text-[#5C534A]">
                        Yes, cancel anytime. Your access continues until the end of the current billing cycle.
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="faq-11" className="border border-[#E8DFD5] rounded-lg overflow-hidden bg-[#FFFBF7]">
                      <AccordionTrigger className="text-left px-4 py-3 hover:bg-[#F5EDE3] flex items-center justify-between text-sm text-[#3D3832]">
                        How do I report a billing issue?
                      </AccordionTrigger>
                      <AccordionContent className="px-4 py-3 bg-[#F5EDE3] text-sm text-[#5C534A]">
                        Contact our support team at billing@makeitmindful.app for any billing concerns.
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="faq-12" className="border border-[#E8DFD5] rounded-lg overflow-hidden bg-[#FFFBF7]">
                      <AccordionTrigger className="text-left px-4 py-3 hover:bg-[#F5EDE3] flex items-center justify-between text-sm text-[#3D3832]">
                        Is there a free trial?
                      </AccordionTrigger>
                      <AccordionContent className="px-4 py-3 bg-[#F5EDE3] text-sm text-[#5C534A]">
                        Yes! New users get a free trial to explore the app before committing to a subscription.
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>

              </div>
            </div>
          </section>

          {/* Final CTA */}
          <FinalCTASection onJoinWaitlist={openWaitlistModal} />

        </main>

        {/* Footer */}
        <footer className="bg-[#3D3832] text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div>
                <h3 className="text-lg font-semibold mb-4 font-display">Make It Mindful</h3>
                <p className="text-[#B8AFA5]">Creating mindful stories for families.</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
                <ul className="space-y-2 text-[#B8AFA5]">
                  <li><Link to="/pricing" className="hover:text-[#9B8AC4] transition-colors">Pricing</Link></li>
                  <li><Link to="/wizard" className="hover:text-[#9B8AC4] transition-colors">Create a Story</Link></li>
                  <li><a href="mailto:hello@makeitmindful.com" className="hover:text-[#9B8AC4] transition-colors">Contact Us</a></li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4">Story Generator</h3>
                <ul className="space-y-2 text-[#B8AFA5]">
                  <li><Link to="/characters" className="hover:text-[#9B8AC4] transition-colors">Characters</Link></li>
                  <li><Link to="/wizard" className="hover:text-[#9B8AC4] transition-colors">Story Wizard</Link></li>
                  <li><Link to="/library" className="hover:text-[#9B8AC4] transition-colors">My Library</Link></li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4">Stay Connected</h3>
                <form onSubmit={handleSubmit} className="flex">
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-l-full text-[#3D3832] bg-white border-none"
                    required
                  />
                  <Button type="submit" className="bg-gradient-to-r from-[#7B68EE] to-[#9B8AC4] hover:from-[#5B4BC7] hover:to-[#8B7AB8] text-white rounded-r-full">
                    Join
                  </Button>
                </form>
                {message && <p className="mt-4 text-sm text-[#B8AFA5]">{message}</p>}
              </div>
            </div>
            <div className="mt-8 pt-8 border-t border-[#5C534A] text-center text-[#B8AFA5]">
              <p>&copy; {new Date().getFullYear()} Make It Mindful. All rights reserved.</p>
            </div>
          </div>
        </footer>

        {/* Email Capture Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setIsModalOpen(false)}>
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-labelledby="waitlist-dialog-title"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="bg-[#FFFBF7] rounded-2xl w-full max-w-md mx-auto relative shadow-2xl border border-[#E8DFD5] paper-texture overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Decorative top border */}
              <div className="h-1.5 bg-gradient-to-r from-[#7B68EE] via-[#9B8AC4] to-[#8B9FD9]" />
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-5 right-4 text-[#8B8279] hover:text-[#3D3832] transition-colors"
                aria-label="Close modal"
              >
                <X className="h-6 w-6" />
              </button>
              <div className="p-8">
                <h2 id="waitlist-dialog-title" className="text-2xl font-bold mb-2 text-[#3D3832] pr-8 font-display">Start Your Mindful Journey</h2>
                <p className="text-[#5C534A] mb-6">Join our waitlist to be the first to know when we launch.</p>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                  type="text"
                  aria-label="Your name"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full p-3 rounded-lg border border-[#E8DFD5] bg-white focus:border-[#7B68EE] focus:ring-[#7B68EE]"
                  />
                  <Input
                  type="email"
                  aria-label="Your email"
                    placeholder="Your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full p-3 rounded-lg border border-[#E8DFD5] bg-white focus:border-[#7B68EE] focus:ring-[#7B68EE]"
                  />
                  <Button type="submit" className="w-full bg-gradient-to-r from-[#7B68EE] to-[#9B8AC4] hover:from-[#5B4BC7] hover:to-[#8B7AB8] text-white py-3 rounded-full font-medium shadow-lg hover:shadow-xl transition-all">
                    Join Waitlist
                  </Button>
                </form>
                {message && <p className="mt-4 text-center text-[#5C534A]">{message}</p>}
              </div>
            </motion.div>
          </div>
        )}

        <Dialog open={isSignInOpen} onOpenChange={setIsSignInOpen}>
          <DialogContent className="sm:max-w-md p-0 bg-transparent border-none shadow-none">
            <DialogTitle className="sr-only">Log in to Make It Mindful</DialogTitle>
            <SignIn
              redirectUrl="/wizard"
              appearance={{
                elements: {
                  rootBox: { width: '100%' },
                  card: { boxShadow: 'none', border: 'none' },
                },
              }}
            />
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  export default LandingPage;
